'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
interface TileVideoProps {
  src: string;
  className?: string;
  posterSrc?: string;
  title?: string;
  /**
   * Set for the first tile that is above the fold so its poster image is
   * eagerly loaded (disables lazy loading) as the LCP candidate.
   */
  priority?: boolean;
}
export function TileVideo(t0) {
  const $ = _c(32);
  const {
    src,
    className,
    posterSrc,
    title,
    priority: t1
  } = t0;
  const priority = t1 === undefined ? false : t1;
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPosterLoaded, setIsPosterLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  let t2;
  let t3;
  if ($[0] !== isInView) {
    t2 = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !isInView) {
            setIsInView(true);
            if (videoRef.current) {
              videoRef.current.load();
            }
          }
        });
      }, {
        rootMargin: "50px"
      });
      observer.observe(container);
      return () => {
        observer.unobserve(container);
      };
    };
    t3 = [isInView];
    $[0] = isInView;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsVideoLoaded(true);
      if (videoRef.current) {
        videoRef.current.play().catch(_temp);
      }
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleVideoLoadedData = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setHasError(true);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleVideoError = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsPosterLoaded(true);
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const handlePosterLoad = t6;
  let t7;
  if ($[6] !== className || $[7] !== hasError || $[8] !== isPosterLoaded || $[9] !== isVideoLoaded) {
    t7 = !isPosterLoaded && !isVideoLoaded && !hasError && <div className={`absolute inset-0 animate-pulse bg-zinc-200 dark:bg-zinc-800 ${className}`} />;
    $[6] = className;
    $[7] = hasError;
    $[8] = isPosterLoaded;
    $[9] = isVideoLoaded;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== className || $[12] !== isPosterLoaded || $[13] !== isVideoLoaded || $[14] !== posterSrc || $[15] !== priority || $[16] !== title) {
    t8 = posterSrc && <Image src={posterSrc} alt={title ? `${title} preview` : "Video thumbnail"} fill={true} sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" className={`${className} transition-opacity duration-300 ${isPosterLoaded && !isVideoLoaded ? "opacity-100" : "opacity-0"}`} onLoad={handlePosterLoad} priority={priority} quality={75} />;
    $[11] = className;
    $[12] = isPosterLoaded;
    $[13] = isVideoLoaded;
    $[14] = posterSrc;
    $[15] = priority;
    $[16] = title;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== className || $[19] !== isInView || $[20] !== isVideoLoaded || $[21] !== src || $[22] !== title) {
    t9 = isInView && <video ref={videoRef} src={src} aria-label={title ? `${title} video` : "Video"} autoPlay={true} loop={true} muted={true} playsInline={true} preload="metadata" onLoadedData={handleVideoLoadedData} onError={handleVideoError} className={`${className} ${isVideoLoaded ? "opacity-100" : "opacity-0"} transition-opacity duration-500`} style={{
      pointerEvents: "none",
      userSelect: "none",
      WebkitUserSelect: "none",
      MozUserSelect: "none"
    }} onContextMenu={_temp2} />;
    $[18] = className;
    $[19] = isInView;
    $[20] = isVideoLoaded;
    $[21] = src;
    $[22] = title;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== className || $[25] !== hasError) {
    t10 = hasError && <div className={`absolute inset-0 bg-zinc-200 dark:bg-zinc-800 ${className} flex items-center justify-center`}><span className="text-xs text-zinc-500">Failed to load</span></div>;
    $[24] = className;
    $[25] = hasError;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t11 = <div ref={containerRef} className="relative size-full">{t7}{t8}{t9}{t10}</div>;
    $[27] = t10;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
}
function _temp2(e) {
  return e.preventDefault();
}
function _temp() {}